// @flow
import React, {Component} from 'react';
import type {Node} from 'react';
import classnames from 'classnames';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {delay} from 'lodash';
import {injectIntl} from 'react-intl';
import {HA_SERP_MIN_MAX_PRICE_FIELDS} from '../../abacus/experiments';
import routerActions from '../../actions/router';
import overlayActions from '../../actions/overlay';
import searchParamsActions from '../../actions/searchParams';
import {
    type SearchParam,
    type SearchParamsType
} from '../../reducers/searchParams';

import type {IntlShape} from 'react-intl';
import type {State as ReduxState} from '../../reducers/index';
import {trigger as edapTrigger, GENERIC_EVENT} from '../../utils/edapWrapper';
import {logExperiment} from '@homeaway/abacus-client-utils';

import InstantConfirmationFilter from '../InstantConfirmationFilter/InstantConfirmationFilter';
import BedroomsFilter, {
    getBedroomValue,
    getBedroomLabel
} from '../BedroomsFilter/BedroomsFilter';
import DropdownPanel from '../DropdownPanel/DropdownPanel';
import {FormattedMessage} from 'react-intl';
import {DropdownControlled} from '@homeaway/react-dropdown';
import PriceFilter from '../PriceFilter/PriceFilter';
import FormattedCurrencyRange from '../FormattedCurrencyRange/FormattedCurrencyRange';
import type {BoundsType} from '../PriceFilter/PriceFilter';
import MoreFiltersPanelDropdown from '../MoreFiltersPanelDropdown/MoreFiltersPanelDropdown';
import {
    instantBookFilter,
    isOnlyInstantConfirmationFilter
} from '../../utils/filters';
import {
    getLimitedMaxDisplayPrice,
    getLimitedMaxPrice,
    getUnlimitedMaxDisplayPrice
} from '../PriceFilter/utils';
import type {Action as OverlayAction} from '../../actions/overlay';
import type {EdapData} from '../../utils/edapWrapper';

import './FilterBar.less';

type FilterBarDropdownProps = {
    isSelected: boolean,
    dropdownClass: string,
    dataWdio?: string,
    children: Node
};

type DispatchProps = {
    filter: typeof routerActions.filter,
    updateInputParams: typeof searchParamsActions.updateInputParams,
    resetInputParams: typeof searchParamsActions.resetInputParams,
    updateFilterCheck: typeof searchParamsActions.updateFilterCheck,
    updatePriceInputValuesValidity: typeof searchParamsActions.updatePriceInputValuesValidity
};

type Props = {
    currency: string,
    hasDates: boolean,
    minBedrooms: number,
    maxBedrooms: number,
    minPrice: number,
    maxPrice: number,
    inputMinBedrooms: number,
    inputMaxBedrooms: number,
    inputMinPrice: number,
    inputMaxPrice: number,
    inputParamsAreDefault: boolean,
    priceInputValuesAreValid: boolean,
    isOverlayVisible: boolean,
    inputParamsAreChanged: boolean,
    isInstantBookingChecked: boolean,
    isMoreFiltersSelected: boolean,
    minMaxPriceFieldsTest?: Object,
    intl: IntlShape,
    showOverlay: () => OverlayAction,
    hideOverlay: () => OverlayAction,
    syncInputParams: typeof searchParamsActions.syncInputParams,
    siteName: string,
    currentBedrooms: number,
    exactBedroomsMatch: boolean,
    searchResultsLoading: boolean,
    topBannersHeight: number,
    locale: string
} & DispatchProps;

type FilterId =
    | 'priceFilter'
    | 'instantConfirmationFilter'
    | 'bedroomsFilter'
    | 'moreFilters';

type State = {
    dropdownOpen: ?FilterId
};

export const PRICE_FILTER_ID = 'priceFilter';
export const INSTANT_CONFIRMATION_FILTER_ID = 'instantConfirmationFilter';
export const BEDROOMS_FILTER_ID = 'bedroomsFilter';
export const MORE_FILTERS_ID = 'moreFilters';

export const EDAP_DROPDOWN_CLICK_LABELS: {[FilterId]: string} = {
    [PRICE_FILTER_ID]: 'filters-price-drop',
    [BEDROOMS_FILTER_ID]: 'filters-beds-drop',
    [INSTANT_CONFIRMATION_FILTER_ID]: 'filters-instant-confirmation-drop'
};

export const EDAP_DROPDOWN_APPLY_LABELS: {[FilterId]: string} = {
    [PRICE_FILTER_ID]: 'price',
    [BEDROOMS_FILTER_ID]: 'beds',
    [INSTANT_CONFIRMATION_FILTER_ID]: 'instant_booking'
};

export function FilterBarDropdown({
    isSelected,
    dropdownClass,
    dataWdio,
    children
}: FilterBarDropdownProps): Node {
    return (
        <div
            data-wdio={dataWdio}
            className={classnames(`FilterBar__dropdown ${dropdownClass}`, {
                'FilterBar__dropdown--selected': isSelected,
                'FilterBar__dropdown--unselected': !isSelected
            })}
        >
            {children}
        </div>
    );
}
// TODO: move handlers for shared logic between FilterForm and FilterBar
// https://jira.homeawaycorp.com/browse/SERP-909
export class FilterBar extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            dropdownOpen: null
        };
    }

    loggedMinMaxPriceFieldsTestFlag: boolean = false;

    edapEvent = (eventObj: EdapData) => {
        edapTrigger(GENERIC_EVENT, eventObj);
    };

    /** Dropdown controllers */

    closeDropdowns = () => {
        const {dropdownOpen} = this.state;

        // manually set the focus
        if (dropdownOpen) {
            this.props.hideOverlay();

            this.setState({
                dropdownOpen: null
            });
        }
    };

    openDropdown = (key: FilterId) => {
        if (
            key === PRICE_FILTER_ID &&
            this.props.minMaxPriceFieldsTest &&
            !this.loggedMinMaxPriceFieldsTestFlag
        ) {
            logExperiment(this.props.minMaxPriceFieldsTest);
            this.loggedMinMaxPriceFieldsTestFlag = true;
        }

        this.setState({
            dropdownOpen: key
        });
        this.props.showOverlay();
    };

    toggleDropdown = (key: FilterId) => () => {
        const {dropdownOpen} = this.state;
        if (key === MORE_FILTERS_ID) {
            this.edapEvent({
                eventaction: 'filter menu clicked'
            });
        } else {
            this.edapEvent({
                eventaction: 'filter dropdown clicked',
                eventlabel: EDAP_DROPDOWN_CLICK_LABELS[key]
            });
        }

        if (dropdownOpen === key) {
            this.applyFilters();
        } else {
            this.openDropdown(key);
        }
    };

    /** End dropdown controllers */

    filterIsOpen = (id: FilterId): boolean => this.state.dropdownOpen === id;

    /**
     * performSearch()
     * Method that accesses `searchParams` action dispatcher
     */
    performSearch = () => {
        this.props.filter();
    };

    /**
     * handleInputParamsBedroomsFilterChange()
     * Handles transient-state click interaction with range filters (bedrooms, bathrooms, etc)
     */
    handleInputParamsBedroomsFilterChange = ({
        minBedrooms,
        maxBedrooms
    }: SearchParamsType) => {
        const {updateInputParams} = this.props;
        updateInputParams({
            minBedrooms,
            maxBedrooms
        });
    };

    resetParams = () => {
        this.edapEvent({
            eventaction: 'all filters cleared'
        });
        this.props.resetInputParams();
        this.props.filter();
    };

    handlePriceChange = (bounds: BoundsType) => {
        const {updateInputParams} = this.props;
        updateInputParams({
            minPrice: bounds[0] || 0,
            maxPrice: bounds[1]
        });
    };

    cancel = () => {
        this.edapEvent({
            eventaction: 'filters canceled'
        });

        this.props.syncInputParams();
        this.closeDropdowns();
    };

    applyFilters = () => {
        const openedDropdown = this.state.dropdownOpen;
        const {
            performSearch,
            closeDropdowns,
            props: {inputParamsAreChanged, updatePriceInputValuesValidity}
        } = this;
        if (inputParamsAreChanged) {
            if (openedDropdown && openedDropdown !== MORE_FILTERS_ID) {
                this.edapEvent({
                    eventaction: 'filters applied',
                    eventlabel: EDAP_DROPDOWN_APPLY_LABELS[openedDropdown]
                });
            } else {
                this.edapEvent({
                    eventaction: 'filters applied'
                });
            }
            performSearch();
        }
        updatePriceInputValuesValidity(true);
        closeDropdowns();
    };

    handleInstantConfirmationApply = (
        event: SyntheticEvent<HTMLInputElement>
    ) => {
        const {updateFilterCheck} = this.props;
        if (!event || !event.currentTarget) {
            return;
        }

        const eventlabel = event.currentTarget.checked
            ? 'instant_booking'
            : 'no_instant_booking';

        this.edapEvent({
            eventaction: 'filters applied',
            eventlabel,
            highfrequency: 'true'
        });

        updateFilterCheck(event.currentTarget);

        this.performSearch();
        delay(this.closeDropdowns, 200);
    };

    /**
     * handleInputParamsRangeFilterChange(), e.g. BathroomsFilter
     * Handles transient-state click interaction with range filters (bedrooms, bathrooms, etc)
     */
    handleInputParamsRangeFilterChange = (values: SearchParamsType) => {
        const {updateInputParams} = this.props;
        updateInputParams(values);
    };

    handleDropdownClear = (paths: SearchParam | SearchParam[]) => () => {
        this.props.resetInputParams(paths);
        this.performSearch();
        this.closeDropdowns();
    };

    rangeParamsNotDefault = (
        minParam?: number,
        maxParam?: ?number
    ): boolean => {
        return minParam !== 0 || maxParam !== null;
    };

    bedroomsDropdownLabel = (showDefault: boolean): string => {
        const {
            intl: {formatMessage},
            minBedrooms = 0,
            maxBedrooms
        } = this.props;
        const exactBedroomsMatch = minBedrooms === maxBedrooms;

        if (showDefault) {
            return formatMessage({
                defaultMessage: 'Bedrooms',
                description: 'toggle bedrooms filter',
                id: 'bedroomsLabel'
            });
        }
        return `${getBedroomValue(
            minBedrooms,
            exactBedroomsMatch,
            formatMessage
        )} ${getBedroomLabel(minBedrooms, exactBedroomsMatch, formatMessage)}`;
    };

    priceDropdownLabel = (showDefault: boolean) => {
        let maxAllowedPrice;
        let maxDisplayPrice;
        let isMax;

        const {
            intl: {formatMessage},
            minPrice,
            maxPrice,
            currency,
            locale,
            minMaxPriceFieldsTest
        } = this.props;

        if (showDefault) {
            return formatMessage({
                defaultMessage: 'Price',
                description: 'filter on the prices of properties',
                id: 'filterBarPriceLabel'
            });
        }

        const isMinMaxPriceFieldsTestV2 =
            (minMaxPriceFieldsTest || {}).value === 2;
        const limitedMaxPrice = getLimitedMaxPrice(currency);

        if (isMinMaxPriceFieldsTestV2) {
            maxAllowedPrice = null;
            maxDisplayPrice = getUnlimitedMaxDisplayPrice(
                limitedMaxPrice,
                maxPrice
            );
            isMax = typeof maxPrice !== 'number';
        } else {
            maxAllowedPrice = limitedMaxPrice;
            maxDisplayPrice = getLimitedMaxDisplayPrice(
                maxAllowedPrice,
                maxPrice
            );
            isMax = maxDisplayPrice >= maxAllowedPrice;
        }

        return (
            <FormattedCurrencyRange
                minPrice={minPrice || 0}
                maxPrice={maxDisplayPrice}
                currency={currency}
                isMax={isMax}
                locale={locale}
            />
        );
    };

    /**
     * TODO: fix localization in <Dropdown label> prop
     */
    render() {
        const {
            applyFilters,
            bedroomsDropdownLabel,
            priceDropdownLabel,
            cancel,
            closeDropdowns,
            filterIsOpen,
            handleInputParamsBedroomsFilterChange,
            handleInputParamsRangeFilterChange,
            handleInstantConfirmationApply,
            rangeParamsNotDefault,
            props: {
                currency,
                hasDates,
                inputMaxBedrooms,
                inputMaxPrice,
                inputMinBedrooms = 0,
                inputMinPrice,
                inputParamsAreDefault,
                intl,
                isInstantBookingChecked,
                isMoreFiltersSelected,
                isOverlayVisible,
                maxBedrooms,
                maxPrice,
                minBedrooms,
                minPrice,
                searchResultsLoading,
                topBannersHeight,
                locale,
                priceInputValuesAreValid
            }
        } = this;

        const {formatMessage} = intl;
        // Dropdown open states
        const isBedroomsFilterOpen = filterIsOpen(BEDROOMS_FILTER_ID);
        const isPriceDropdownOpen = filterIsOpen(PRICE_FILTER_ID);
        const isInstantConfirmationFilterOpen = filterIsOpen(
            INSTANT_CONFIRMATION_FILTER_ID
        );
        const isMoreFiltersOpen = filterIsOpen(MORE_FILTERS_ID);

        // Dropdown active states
        const isBedroomsFilterActive = rangeParamsNotDefault(
            minBedrooms,
            maxBedrooms
        );
        const isPriceFilterActive = rangeParamsNotDefault(minPrice, maxPrice);

        // Dropdown clear visible states
        const isBedroomsFilterClearVisible = rangeParamsNotDefault(
            inputMinBedrooms,
            inputMaxBedrooms
        );
        const isPriceFilterClearVisible =
            !priceInputValuesAreValid ||
            rangeParamsNotDefault(inputMinPrice, inputMaxPrice);

        return (
            <div className="FilterBar" data-wdio="FilterBar">
                <FilterBarDropdown
                    dropdownClass="PriceDropdown"
                    dataWdio="PriceDropdown"
                    isSelected={isPriceFilterActive}
                >
                    <DropdownControlled
                        id={PRICE_FILTER_ID}
                        label={priceDropdownLabel(!isPriceFilterActive)}
                        onDropdownClick={this.toggleDropdown(PRICE_FILTER_ID)}
                        onBlur={applyFilters}
                        onTouch={applyFilters}
                        open={isPriceDropdownOpen}
                    >
                        {isPriceDropdownOpen && (
                            <DropdownPanel
                                onApply={applyFilters}
                                onClear={this.handleDropdownClear([
                                    'minPrice',
                                    'maxPrice'
                                ])}
                                showButtons
                                clearVisible={isPriceFilterClearVisible}
                                submitDisabled={!priceInputValuesAreValid}
                            >
                                <PriceFilter
                                    currency={currency}
                                    hasDates={hasDates}
                                    onPriceChange={this.handlePriceChange}
                                    minPrice={inputMinPrice}
                                    maxPrice={inputMaxPrice}
                                    locale={locale}
                                    className="PriceFilterDropdown_PriceFilter"
                                />
                            </DropdownPanel>
                        )}
                    </DropdownControlled>
                </FilterBarDropdown>
                <FilterBarDropdown
                    dropdownClass="BedroomsDropdown"
                    dataWdio="BedroomsDropdown"
                    isSelected={isBedroomsFilterActive}
                >
                    <DropdownControlled
                        id={BEDROOMS_FILTER_ID}
                        label={bedroomsDropdownLabel(!isBedroomsFilterActive)}
                        onDropdownClick={this.toggleDropdown(
                            BEDROOMS_FILTER_ID
                        )}
                        onBlur={applyFilters}
                        onTouch={applyFilters}
                        open={isBedroomsFilterOpen}
                    >
                        {isBedroomsFilterOpen && (
                            <DropdownPanel
                                showButtons
                                onApply={applyFilters}
                                onClear={this.handleDropdownClear([
                                    'minBedrooms',
                                    'maxBedrooms'
                                ])}
                                clearVisible={isBedroomsFilterClearVisible}
                            >
                                <BedroomsFilter
                                    minBedrooms={inputMinBedrooms}
                                    maxBedrooms={inputMaxBedrooms}
                                    onChange={
                                        handleInputParamsBedroomsFilterChange
                                    }
                                    className="BedroomsDropdown__BedroomsFilter"
                                />
                            </DropdownPanel>
                        )}
                    </DropdownControlled>
                </FilterBarDropdown>
                <FilterBarDropdown
                    dropdownClass="InstantConfirmationDropdown"
                    dataWdio="InstantConfirmationDropdown"
                    isSelected={isInstantBookingChecked}
                >
                    <DropdownControlled
                        id={INSTANT_CONFIRMATION_FILTER_ID}
                        label={formatMessage({
                            defaultMessage: 'Instant Confirmation',
                            description:
                                'toggle instant confirmation properties',
                            id: 'instantConfirmationPrimaryLabel'
                        })}
                        onDropdownClick={this.toggleDropdown(
                            INSTANT_CONFIRMATION_FILTER_ID
                        )}
                        onBlur={closeDropdowns}
                        onTouch={closeDropdowns}
                        open={isInstantConfirmationFilterOpen}
                    >
                        {isInstantConfirmationFilterOpen && (
                            <DropdownPanel>
                                <InstantConfirmationFilter
                                    id="instantConfirmation__switch"
                                    intl={intl}
                                    checked={isInstantBookingChecked}
                                    handleChecked={
                                        handleInstantConfirmationApply
                                    }
                                    className="InstantConfirmationDropdown_InstantConfirmationFilter"
                                />
                            </DropdownPanel>
                        )}
                    </DropdownControlled>
                </FilterBarDropdown>
                <FilterBarDropdown
                    dropdownClass={'MoreFiltersPanelDropdown'}
                    isSelected={isMoreFiltersSelected}
                >
                    <MoreFiltersPanelDropdown
                        toggleDropdown={this.toggleDropdown(MORE_FILTERS_ID)}
                        onApplyFilters={applyFilters}
                        onCancel={cancel}
                        onInputParamsRangeFilterChange={
                            handleInputParamsRangeFilterChange
                        }
                        open={isMoreFiltersOpen}
                        searchResultsLoading={searchResultsLoading}
                        topBannersHeight={topBannersHeight}
                    />
                </FilterBarDropdown>
                {!inputParamsAreDefault && (
                    <button
                        className={classnames('FilterBar__clear btn btn-link', {
                            invisible: isOverlayVisible
                        })}
                        data-wdio="FilterBar__clear"
                        onClick={this.resetParams}
                    >
                        <FormattedMessage
                            id="formModalResetLabel"
                            description="Button to reset filters"
                            defaultMessage="Clear Filters"
                        />
                    </button>
                )}
            </div>
        );
    }
}

export const mapStateToProps = (state: ReduxState) => {
    // select params that only apply to this component
    const {
        abacus,
        searchParams: {
            params: {minBedrooms, maxBedrooms, minPrice, maxPrice},
            inputParams,
            inputParamsAreDefault,
            inputParamsAreChanged,
            priceInputValuesAreValid,
            params: {filter, minBathrooms, maxBathrooms}
        },
        system: {currency, siteName, fullLocale},
        topBanners: {height: topBannersHeight},
        overlay: {showOverlay: isOverlayVisible}
    } = state;

    return {
        currency,
        hasDates: inputParams.arrival && inputParams.departure,
        minBedrooms,
        maxBedrooms,
        minPrice,
        maxPrice,
        inputMinBedrooms: inputParams.minBedrooms,
        inputMaxBedrooms: inputParams.maxBedrooms,
        inputMinPrice: inputParams.minPrice,
        inputMaxPrice: inputParams.maxPrice,
        inputParamsAreChanged,
        minMaxPriceFieldsTest: abacus[HA_SERP_MIN_MAX_PRICE_FIELDS],
        siteName,
        topBannersHeight,
        inputParamsAreDefault,
        priceInputValuesAreValid,
        isOverlayVisible,
        isInstantBookingChecked: !!instantBookFilter(filter),
        isMoreFiltersSelected: !!(
            (minBathrooms && minBathrooms > 0) ||
            (maxBathrooms && maxBathrooms > 0) ||
            (filter &&
                filter.length > 0 &&
                !isOnlyInstantConfirmationFilter(filter))
        ),
        locale: fullLocale
    };
};

const mapDispatchToProps = {
    filter: routerActions.filter,
    showOverlay: () => overlayActions.toggleOverlay(true),
    hideOverlay: () => overlayActions.toggleOverlay(false),
    resetInputParams: searchParamsActions.resetInputParams,
    syncInputParams: searchParamsActions.syncInputParams,
    updateInputParams: searchParamsActions.updateInputParams,
    updateFilterCheck: searchParamsActions.updateFilterCheck,
    updatePriceInputValuesValidity:
        searchParamsActions.updatePriceInputValuesValidity
};

export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(FilterBar);
